import { TransactionBuilder } from '@orca-so/common-sdk'
import { ASSOCIATED_TOKEN_PROGRAM_ID, NATIVE_MINT, Token, TOKEN_PROGRAM_ID } from '@solana/spl-token'
import { PublicKey, SystemProgram } from '@solana/web3.js'
import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

import { useBalances } from '@/hooks/use-balances'
import useDepositTokenOptions from '@/hooks/use-deposit-token-options'
import useNemoWallet from '@/hooks/use-nemo-wallet'
import useSafelySendTxWithNotice from '@/hooks/use-safely-send-tx-with-notice'
import { toU64 } from '@/utils/cypress-support/utils'
import type { CustomTokenInfo } from '@/utils/helpers'
import { useConnection } from '@/wallet/adapter'

import { NetworkOption } from './types'
import { networkOptions } from './utils'

export type ContextType = {
  selectedToken: CustomTokenInfo | null
  setSelectedToken: Dispatch<SetStateAction<CustomTokenInfo | null>>
  // selectedTokenInfo?: APITokenInfo
  submitWithdrawByRenecNetwork: (toWalletAddress: string, tokenAmount: number) => Promise<void>,
  currentTokenBalance: number
  isSubmitSucceeded: boolean
  setIsSubmitSucceeded: Dispatch<SetStateAction<boolean>>
  isLoading: boolean
  tokenOptions: CustomTokenInfo[]
  networkOptions: NetworkOption[]
}

export const initValue: ContextType = {
  selectedToken: null,
  setSelectedToken: () => null,
  submitWithdrawByRenecNetwork: () => Promise.resolve(),
  currentTokenBalance: 0,
  isSubmitSucceeded: false,
  setIsSubmitSucceeded: () => null,
  isLoading: false,
  tokenOptions: [],
  networkOptions: [],
}

export const AssetWithdrawalContext = createContext<ContextType>(initValue)
export const AssetWithdrawalProvider = ({ children }: HocProps) => {
  const { balances } = useBalances()
  const { connection } = useConnection()
  const { anchorWallet, publicKey: senderWalletPubkey } = useNemoWallet()
  const { safelySendTxWithNotice } = useSafelySendTxWithNotice('make_asset_withdrawal')
  const { tokenOptions, isLoading: isLoadingTokenOptions } = useDepositTokenOptions()
  const [selectedToken, setSelectedToken] = useState<CustomTokenInfo | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitSucceeded, setIsSubmitSucceeded] = useState(false)
  const isLoading = isLoadingTokenOptions || isSubmitting

  const currentTokenBalance = useMemo(() => {
    const tokenSymbol = selectedToken?.symbol

    if (!tokenSymbol) {
      return 0
    }

    return balances?.[tokenSymbol] || 0
  }, [balances, selectedToken])

  useEffect(() => {
    if (!selectedToken) {
      setIsSubmitting(false)
      setIsSubmitSucceeded(false)
    }
  }, [selectedToken])

  const submitWithdrawByRenecNetwork = useCallback(async (toWalletAddress: string, tokenAmount: number) => {
    if (isSubmitting) return

    setIsSubmitting(true)

    if (!selectedToken) {
      throw Error('[assets:withdraw] no token info')
    }

    if (!senderWalletPubkey || !toWalletAddress) {
      throw Error('[assets:withdraw] missing wallet addresses')
    }

    if (!tokenAmount) {
      throw Error('[assets:withdraw] input amount')
    }

    const tokenMintPubkey = new PublicKey(selectedToken.address)
    const fromWallet = senderWalletPubkey
    const toWallet = new PublicKey(toWalletAddress)

    const txBuilder = new TransactionBuilder(connection, anchorWallet)

    if (tokenMintPubkey.equals(NATIVE_MINT)) {
      txBuilder.addInstruction({
        cleanupInstructions: [],
        instructions: [
          SystemProgram.transfer({
            fromPubkey: fromWallet,
            toPubkey: toWallet,
            lamports: toU64(tokenAmount, selectedToken.decimals).toNumber(),
          }),
        ],
        signers: [],
      })
    } else {
      const senderATA = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        tokenMintPubkey,
        fromWallet,
      )
      const receiverATA = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        tokenMintPubkey,
        toWallet,
        true,
      )

      // Initialize receiverATA if not yet created.
      const receiverATAInfo = await connection.getAccountInfo(receiverATA)
      if (!receiverATAInfo) {
        txBuilder.addInstruction({
          cleanupInstructions: [],
          instructions: [
            Token.createAssociatedTokenAccountInstruction(
              ASSOCIATED_TOKEN_PROGRAM_ID,
              TOKEN_PROGRAM_ID,
              tokenMintPubkey,
              receiverATA,
              toWallet,
              fromWallet,
            ),
          ],
          signers: [],
        })
      }

      txBuilder.addInstruction({
        cleanupInstructions: [],
        instructions: [
          Token.createTransferInstruction(
            TOKEN_PROGRAM_ID,
            senderATA,
            receiverATA,
            fromWallet,
            [],
            toU64(tokenAmount, selectedToken.decimals).toNumber(),
          ),
        ],
        signers: [],
      })
    }

    const txid = await safelySendTxWithNotice(txBuilder)
    if (txid) {
      setIsSubmitSucceeded(true)
    }

    setIsSubmitting(false)
  }, [
    selectedToken,
    anchorWallet,
    connection,
    isSubmitting,
    senderWalletPubkey,
    safelySendTxWithNotice,
  ])

  const ctxValue = useMemo(() => ({
    selectedToken,
    setSelectedToken,
    currentTokenBalance,
    submitWithdrawByRenecNetwork,
    isLoading,
    isSubmitSucceeded,
    setIsSubmitSucceeded,
    tokenOptions,
    networkOptions,
  }), [
    selectedToken,
    setSelectedToken,
    currentTokenBalance,
    submitWithdrawByRenecNetwork,
    isLoading,
    isSubmitSucceeded,
    setIsSubmitSucceeded,
    tokenOptions,
  ])

  return (
    <AssetWithdrawalContext.Provider value={ctxValue}>
      {children}
    </AssetWithdrawalContext.Provider>
  )
}
